import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import useSupabaseClient from '../hooks/useSupabaseClient';
import DraggableDialogPaper from './DraggableDialogPaper';

const InvoiceAccountingEntryPickerModal = ({ open, onClose, onAddEntries, userId }) => {
  const { supabase } = useSupabaseClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (!open) {
      setError('');
      setInvoices([]);
      setSelectedInvoice(null);
      setEntries([]);
      setInvoiceSearch('');
      return;
    }

    const fetchInvoices = async () => {
      setIsLoading(true);
      setError('');
      try {
        if (!userId) throw new Error('Usuario no autenticado');

        let query = supabase
          .from('invoices')
          .select('id, invoice_number, date, billing_name, total')
          .eq('user_id', userId)
          .order('date', { ascending: false })
          .order('id', { ascending: false })
          .limit(50);

        if (invoiceSearch && invoiceSearch.trim()) {
          const q = invoiceSearch.trim();
          query = query.or(`invoice_number.ilike.%${q}%,billing_name.ilike.%${q}%`);
        }

        const { data, error: qErr } = await query;
        if (qErr) throw qErr;

        setInvoices(data || []);
      } catch (e) {
        setError(e.message || 'Error al cargar facturas');
        setInvoices([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [open, supabase, userId, invoiceSearch]);

  useEffect(() => {
    if (!open) return;
    if (!selectedInvoice?.id) {
      setEntries([]);
      return;
    }

    const fetchEntries = async () => {
      setIsLoading(true);
      setError('');
      try {
        const { data, error: qErr } = await supabase
          .from('accounting_entries')
          .select('*')
          .eq('user_id', userId)
          .eq('invoice_id', selectedInvoice.id)
          .order('entry_date', { ascending: true })
          .order('id', { ascending: true });

        if (qErr) throw qErr;
        setEntries(data || []);
      } catch (e) {
        setError(e.message || 'Error al cargar asientos');
        setEntries([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntries();
  }, [open, supabase, userId, selectedInvoice?.id]);

  const totals = useMemo(() => {
    const debit = entries.reduce((acc, e) => acc + (Number(e.debit) || 0), 0);
    const credit = entries.reduce((acc, e) => acc + (Number(e.credit) || 0), 0);
    return { debit, credit, diff: debit - credit };
  }, [entries]);

  const handleClone = () => {
    const mapped = entries.map(r => ({
      source_accounting_entry_id: r.id,
      invoice_id: r.invoice_id ?? null,
      entry_date: r.entry_date,
      account_code: r.account_code,
      account_name: r.account_name,
      description: r.description ?? '',
      debit: r.debit ?? 0,
      credit: r.credit ?? 0,
      third_party_nit: r.third_party_nit ?? '',
      third_party_name: r.third_party_name ?? '',
      cost_center_code: r.cost_center_code ?? '',
      document_reference: r.document_reference ?? '',
      base_amount: r.base_amount ?? null,
      tax_rate: r.tax_rate ?? null,
      observation: r.observation ?? ''
    }));

    onAddEntries(mapped);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperComponent={DraggableDialogPaper}>
      <DialogTitle className="draggable-dialog-title" sx={{ cursor: 'move' }}>Copiar asientos desde facturas</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2, mt: 1 }}>
          <TextField
            label="Buscar factura (número o cliente)"
            size="small"
            value={invoiceSearch}
            onChange={(e) => setInvoiceSearch(e.target.value)}
            sx={{ minWidth: 320, flex: 1 }}
          />
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Box sx={{ border: '1px solid #eee', borderRadius: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow
                    key={inv.id}
                    hover
                    selected={selectedInvoice?.id === inv.id}
                    onClick={() => setSelectedInvoice(inv)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{inv.date}</TableCell>
                    <TableCell>{inv.invoice_number}</TableCell>
                    <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {inv.billing_name}
                    </TableCell>
                    <TableCell align="right">{Number(inv.total || 0).toLocaleString('es-CO')}</TableCell>
                  </TableRow>
                ))}
                {!isLoading && invoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No hay facturas</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ border: '1px solid #eee', borderRadius: 1, overflow: 'auto' }}>
            <Box sx={{ p: 1 }}>
              <Typography variant="subtitle2">
                Asiento contable {selectedInvoice ? `de factura ${selectedInvoice.invoice_number}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Débitos: {totals.debit.toLocaleString('es-CO')} | Créditos: {totals.credit.toLocaleString('es-CO')}
              </Typography>
            </Box>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Cuenta</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">Débito</TableCell>
                  <TableCell align="right">Crédito</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.entry_date}</TableCell>
                    <TableCell>{r.account_code}</TableCell>
                    <TableCell>{r.account_name}</TableCell>
                    <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.description}
                    </TableCell>
                    <TableCell align="right">{Number(r.debit || 0).toLocaleString('es-CO')}</TableCell>
                    <TableCell align="right">{Number(r.credit || 0).toLocaleString('es-CO')}</TableCell>
                  </TableRow>
                ))}
                {!isLoading && selectedInvoice && entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">La factura no tiene asientos todavía</TableCell>
                  </TableRow>
                )}
                {!isLoading && !selectedInvoice && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Selecciona una factura a la izquierda</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleClone}
            disabled={!selectedInvoice || entries.length === 0}
          >
            Clonar asiento
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceAccountingEntryPickerModal;
